import { UploadIcon, XIcon, FileIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
} from '@/components/ui/file-upload'
import { cn } from '@/lib/utils'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { useState } from 'react'

type Props = {
  value: File[]
  onChange: (files: File[]) => void
  previousLength?: number
  maxFiles?: number
  maxSize?: number
  accept?: string
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>

const FileUploadQuestion = ({
  value,
  onChange,
  previousLength = 0,
  maxFiles = 5,
  maxSize = 10 * 1024 * 1024,
  accept = 'image/*',
  className,
  style,
}: Props) => {
  const [rejection, setRejection] = useState<{
    fileName: string
    message: string
  } | null>(null)

  return (
    <FileUpload
      value={value}
      onValueChange={onChange}
      onFileReject={(file, message) =>
        setRejection({ fileName: file.name, message })
      }
      accept={accept}
      maxFiles={maxFiles}
      maxSize={maxSize}
      multiple
      className='w-full'
    >
      <FileUploadList>
        {value.map((file) => (
          <FileUploadItem
            key={file.name + file.size}
            value={file}
            className={cn('bg-background border-primary/50', className)}
            style={style}
          >
            <FileUploadItemPreview>
              <FileIcon className='h-4 w-4' />
            </FileUploadItemPreview>
            <FileUploadItemMetadata />
            <FileUploadItemDelete asChild>
              <Button variant='ghost' size='icon' className='h-7 w-7 shrink-0'>
                <XIcon className='h-4 w-4' />
              </Button>
            </FileUploadItemDelete>
          </FileUploadItem>
        ))}
      </FileUploadList>

      {value.length + previousLength < maxFiles ? (
        <FileUploadDropzone
          className={cn(
            'min-h-[100px] cursor-pointer bg-background hover:bg-background/70',
            className,
          )}
          style={style}
        >
          <div className='flex flex-col items-center gap-2 text-center'>
            <UploadIcon className='h-8 w-8 text-muted-foreground' />
            <div className='text-sm text-muted-foreground'>
              <span className='font-semibold'>Click to upload</span> or drag and
              drop
            </div>
            <div className='text-xs text-muted-foreground'>
              Max {maxFiles} files, up to {Math.round(maxSize / 1024 / 1024)}MB
              each
            </div>
          </div>
        </FileUploadDropzone>
      ) : (
        <Alert className='mb-4'>
          <AlertTitle>Max files reached</AlertTitle>
          <AlertDescription>
            You have reached the maximum number of files allowed.
          </AlertDescription>
        </Alert>
      )}

      {rejection && (
        <Alert variant='destructive' className='my-4'>
          <AlertTitle>Upload rejected</AlertTitle>
          <AlertDescription>
            {rejection.fileName}: {rejection.message}
          </AlertDescription>
        </Alert>
      )}
    </FileUpload>
  )
}

export default FileUploadQuestion
